<script lang="ts" setup>
import axios from 'axios'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useAuthStore } from '@/stores/auth-store'
import { useTransactionStore } from '@/stores/transaction-store'
import { MinimalResource, uuid } from '@/types/general'
import { DropdownStringOption } from '@/types/inputs'
import { ManageTransaction, TransactionType } from '@/types/transaction'
import { useCompanyStore } from '@/stores/company-store'
import { LicenseType, MinimalCustomer, MinimalLocation } from '@/types/company'
import { addressString } from '@/utils/string'

import MyFormWizardStep from '@/components/my-components/form/MyFormWizardStep.vue'
import MySelect from '@/components/my-components/form/MySelect.vue'
import MyCheckbox from '@/components/my-components/form/MyCheckbox.vue'
import MyInputLabel from '@/components/my-components/form/MyInputLabel.vue'

export interface Props {
    active: boolean
}

const props = defineProps<Props>()

const { t } = useI18n()
const authStore = useAuthStore()
const companyStore = useCompanyStore()
const transaction = useTransactionStore().$state.transaction! as ManageTransaction

const receiverLocations = ref<DropdownStringOption[]>([])
const haulierLocations = ref<DropdownStringOption[]>([])
const sendingCompanyLocations = ref<DropdownStringOption[]>([])
const customers = ref<DropdownStringOption[]>([])
const haulierCompanies = ref<DropdownStringOption[]>([])
const errors = ref<Record<string, string>>({})

const haulierOptions = computed(() =>
    haulierCompanies.value.filter((haulier) => haulier.value !== transaction.receivingCompanyId),
)

const transitTradesEnabled = computed(() => {
    const packagingLicense = authStore.licenses[LicenseType.PackagingModule]

    return packagingLicense?.transitLocationTrades === true
})

const unfilteredCompanyLocations = computed<DropdownStringOption[]>(() => {
    if (sendingCompanyLocations.value.length > 0) {
        return sendingCompanyLocations.value
    }

    return companyStore.locations.map((location) => ({
        value: location.id,
        label: location.name,
    }))
})

const companyLocations = computed<DropdownStringOption[]>(() => {
    let locations = unfilteredCompanyLocations.value
    const userLocations = authStore.company.locations

    if (authStore.hasLicense(LicenseType.AgentLocationFiltering) && userLocations.length > 0) {
        locations = locations.filter((location) => {
            if (location.id === transaction.sendingLocationId) return true
            return userLocations.includes(location.value as string)
        })
    }

    return locations
})

function toggleTransitTrade() {
    if (transaction.type === TransactionType.Transit) {
        transaction.type = TransactionType.Default
    } else {
        transaction.type = TransactionType.Transit
        transaction.shipperLocationId = null
    }
}

async function fetch() {
    fetchCustomers()
    if (transaction.type === TransactionType.Transit && haulierCompanies.value.length === 0) {
        fetchHauliers()
    }

    if (transaction.receivingCompanyId) {
        receiverLocations.value = await fetchLocations(transaction.receivingCompanyId)
    }

    if (transaction.haulierCompanyId) {
        haulierLocations.value = await fetchLocations(transaction.haulierCompanyId)
    }

    if (!transaction.sendingLocationId) {
        transaction.sendingLocationId = companyLocations.value[0].value
    }

    // When editing a transaction as the haulier on the transaction
    // we'll need to fetch the locations for the sender instead of using our own
    if (transaction.sendingCompanyId !== authStore.companyId) {
        sendingCompanyLocations.value = await fetchLocations(transaction.sendingCompanyId)
    }
}

async function fetchLocations(id: uuid): Promise<DropdownStringOption[]> {
    const params = { forCompanyId: transaction.sendingCompanyId }

    const response = await axios.get<MinimalLocation[]>(
        window.route('minimal.companies.locations', {
            company: id,
            ...params,
        }),
    )

    return response.data.map((location) => {
        const fullAddress = addressString(location)
        return {
            value: location.id,
            label: location.name,
            searchValue: `${location.name} ${fullAddress} ${location.pNumber ?? ''}`,
            pNumber: location.pNumber,
            locationAddress: fullAddress,
        }
    })
}

async function fetchCustomers() {
    const response = await axios.get<MinimalCustomer[]>(
        window.route('minimal.companies.customers', { company: transaction.sendingCompanyId }),
    )

    customers.value = response.data.map((customer) => ({
        value: customer.id,
        label: customer.name,
        searchValue: `${customer.name} ${customer.accountNumber ?? ''}`,
        accountNumber: customer.accountNumber,
    }))
    customers.value.unshift({ value: authStore.companyId, label: authStore.company.name })
}

async function fetchHauliers() {
    const response = await axios.get<MinimalResource[]>(
        window.route('minimal.companies.customers', {
            company: authStore.companyId,
            haulier: true,
        }),
    )

    haulierCompanies.value = response.data.map((haulier) => ({
        value: haulier.id,
        label: haulier.name,
    }))
}

function submit() {
    errors.value = {}
    if (!transaction.sendingLocationId) {
        errors.value['sendingLocationId'] = t('sendingLocationError')
    }

    if (!transaction.receivingLocationId) {
        errors.value['receivingLocationId'] = t('receivingLocationError')
    }

    if (transaction.type === TransactionType.Transit && !transaction.haulierLocationId) {
        errors.value['haulierLocationId'] = t('haulierLocationError')
    }

    return Object.keys(errors.value).length === 0
}

watch(
    () => transaction.receivingCompanyId,
    async () => {
        transaction.receivingLocationId = null
        if (transaction.receivingCompanyId === authStore.companyId) {
            receiverLocations.value = unfilteredCompanyLocations.value.filter(
                (location) => location.value !== transaction.sendingLocationId,
            )
        } else if (transaction.receivingCompanyId) {
            delete errors.value['receivingLocationId']
            receiverLocations.value = await fetchLocations(transaction.receivingCompanyId)
            transaction.receivingLocationId = receiverLocations.value[0]?.value || null
        } else {
            receiverLocations.value = []
        }
    },
)

watch(
    () => transaction.sendingLocationId,
    async () => {
        if (
            transaction.receivingLocationId === transaction.sendingLocationId &&
            transaction.receivingCompanyId
        ) {
            transaction.receivingLocationId = null
        }
        if (transaction.receivingCompanyId === authStore.companyId) {
            receiverLocations.value = companyLocations.value.filter(
                (location) => location.value != transaction.sendingLocationId,
            )
        }
    },
)

watch(
    () => transaction.haulierCompanyId,
    async () => {
        transaction.haulierLocationId = null

        if (transaction.haulierCompanyId) {
            delete errors.value['haulierLocationId']
            haulierLocations.value = await fetchLocations(transaction.haulierCompanyId)
            transaction.haulierLocationId = haulierLocations.value[0]?.value || null
        } else {
            haulierLocations.value = []
        }
    },
)

onMounted(fetch)

watch(
    () => transaction.type,
    async () => {
        if (transaction.type === TransactionType.Transit) {
            if (haulierCompanies.value.length === 0) {
                await fetchHauliers()
            }

            if (transaction.haulierCompanyId === null) {
                transaction.haulierCompanyId = haulierOptions.value[0]?.value || null
            }
        } else {
            transaction.haulierCompanyId = null
            transaction.haulierLocationId = null
        }
    },
)
</script>

<template>
    <MyFormWizardStep id="company" :active="props.active" :submit="submit" :title="t('company')">
        <div v-if="transaction" class="grid grid-cols-2 justify-between gap-6">
            <div>
                <div class="mb-1 flex items-center gap-1">
                    <MyInputLabel v-text="t('sender')" />
                    <mdi:information v-tooltip="t('transactionSenderCompanyExplanation')" />
                </div>
                <h4
                    class="mt-0 flex h-10 cursor-default items-center rounded-xl border border-primary-200 pl-2 text-sm font-semibold text-stone-600 dark:border-dark-400 dark:text-primary-200"
                    v-text="transaction.sendingCompanyName"
                />
            </div>

            <MySelect
                v-model="transaction.sendingLocationId"
                :options="companyLocations"
                :error="errors['sendingLocationId'] ?? undefined"
                searchable
            >
                <template #label>
                    <span class="inline-flex items-center gap-x-1">
                        <MyInputLabel v-text="t('sendingLocation')" />
                        <mdi:information v-tooltip="t('transactionSenderLocationExplanation')" />
                    </span>
                </template>
            </MySelect>

            <MySelect v-model="transaction.receivingCompanyId" :options="customers" searchable>
                <template #label>
                    <span class="inline-flex items-center gap-x-1">
                        <MyInputLabel v-text="t('receiver')" />
                        <mdi:information v-tooltip="t('transactionReceiverCompanyExplanation')" />
                    </span>
                </template>

                <template #option="{ option }">
                    <div class="flex justify-between items-center w-full min-w-0">
                        <span
                            v-truncate-tooltip
                            class="flex flex-col w-full min-w-0 truncate"
                            v-text="option.label"
                        />
                        <span
                            v-if="option.accountNumber"
                            class="dark:text-primary-200 text-primary-600 ml-2"
                            v-text="option.accountNumber"
                        />
                    </div>
                </template>
            </MySelect>

            <MySelect
                v-model="transaction.receivingLocationId"
                :options="receiverLocations"
                :error="errors['receivingLocationId'] ?? undefined"
                :disabled="transaction.receivingCompanyId === null"
                searchable
            >
                <template #label>
                    <span class="inline-flex items-center gap-x-1">
                        <MyInputLabel v-text="t('receivingLocation')" />
                        <mdi:information v-tooltip="t('transactionReceiverLocationExplanation')" />
                    </span>
                </template>

                <template #option="{ option }">
                    <div class="flex justify-between items-center w-full min-w-0">
                        <div class="flex flex-col w-full min-w-0">
                            <span v-truncate-tooltip class="truncate" v-text="option.label" />
                            <span
                                v-if="option.locationAddress"
                                v-truncate-tooltip
                                class="truncate"
                                v-text="option.locationAddress"
                            />
                        </div>

                        <span
                            v-if="option.pNumber"
                            class="dark:text-primary-200 text-primary-600 ml-2"
                            v-text="option.pNumber"
                        />
                    </div>
                </template>
            </MySelect>

            <div v-if="transitTradesEnabled" class="flex space-x-2 col-span-2">
                <MyCheckbox
                    :checked="transaction.type === TransactionType.Transit"
                    :label="t('transitTransaction')"
                    @change="toggleTransitTrade"
                />

                <mdi:information v-tooltip="t('transitTradesExplanation')" />
            </div>

            <MySelect
                v-if="transaction.type === TransactionType.Transit"
                v-model="transaction.haulierCompanyId"
                :options="haulierOptions"
                :label="t('haulier')"
                searchable
                clear-button
            />

            <MySelect
                v-if="transaction.type === TransactionType.Transit"
                v-model="transaction.haulierLocationId"
                :options="haulierLocations"
                :label="t('haulierLocation')"
                :error="errors['haulierLocationId'] ?? undefined"
                :disabled="transaction.haulierCompanyId === null"
                searchable
            />
        </div>
    </MyFormWizardStep>
</template>
